import React, { useMemo, useState } from 'react';
import { Box, Button, ClickAwayListener, Fade, Grid, Paper, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { DisableAppScroll, EnableAppScroll } from 'app/layout/Layout';
import { useEffect } from 'react';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import _, { isArray, throttle } from 'lodash';
import { CustomTypeAhead, ISelection } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/CustomTypeAhead';
import { useDispatch, useSelector } from 'react-redux';
import { notificationDataActions } from 'store/redux-store/notification/notification.redux';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { PreferenceCosmosKeys } from 'oidc/UserDevicePreference/utils';
const useStyles = makeStyles<{
  isSelected: boolean;
  open: boolean;
  anchorRef: boolean;
  removeActionContainer: boolean;
}>()((theme, { isSelected, open, anchorRef, removeActionContainer }) => ({
  root: {},
  popper: {
    marginTop: 4,
    zIndex: 5000,
  },
  paperDropdown: {
    boxShadow: '0px 2px 8px #0000004D',
  },
  headerContainer: {
    minWidth: 250,
    padding: '12px 20px 6px',
  },
  headerContainerNoSearchBoxSingleOption: {
    minWidth: 250,
    padding: '0px 20px 0px',
  },
  actionContainer: {
    visibility: removeActionContainer ? 'hidden' : undefined,
    backgroundColor: '#ffffff',
    borderTop: removeActionContainer ? undefined : '1px solid #cccccc',
    padding: removeActionContainer ? 0 : '6px 20px',
    height: removeActionContainer ? 0 : undefined,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  button: {
    fontSize: 14,
    textTransform: 'none',
    '&:hover, &:focus': {
      background: 'none',
    },
  },
  inActive: {
    color: '#888888',
  },
  clearAllButton: {
    padding: 0,
  },
  actionButton: {
    padding: '6px 0px',
  },
  countTag: {
    color: '#555555',
    fontSize: 14,
    fontWeight: 600,
  },
  filterIcon: {
    padding: '0px',
    border: `1px solid ${open ? theme.palette.framework.system.skyBlue : theme.palette.system.lightGrey}`,
    color: open ? theme.palette.framework.system.skyBlue : theme.palette.framework.system.charcoal,
    minWidth: 38,
    minHeight: 38,
    marginTop: 14,
    '&:hover': {
      background: open ? 'none' : theme.palette.system.lightblueBackground,
      border: `1px solid ${theme.palette.framework.system.skyBlue}`,
      color: theme.palette.framework.system.skyBlue,
    },
  },
  filterApplied: {
    padding: '0px',
    minWidth: 38,
    minHeight: 38,
    marginTop: 14,
    background: theme.palette.system.lightblueBackground,
    border: `1px solid ${theme.palette.framework.system.skyBlue}`,
    color: theme.palette.framework.system.skyBlue,
  },
  listItem: {
    fontSize: 14,
    // '& .MuiAutocomplete-listbox-listBox': {
    //   fontSize: 14,
    // },
    // '& .MuiAutocomplete-option ': {
    //   fontSize: 14,
    // },
  },
}));

export const AVOID_CLICK_AWAY_LABEL = 'avoid-click-away-type-ahead-drop-down';

export const InboxMessageFilter = props => {
  const {
    isMultiSelect,
    value: selected,
    selectAllValue,
    options: providedOptions,
    filterName,
    hiddenInput,
    isDisabled,
    anchorRef,
    removeActionContainer = false,
    customClass,
    getOptionDisabled,
    searchBoxHiddenWithSingleOption = false,
  } = props;
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [options, setOptions] = useState<ISelection[]>([]);
  const trackerArea = `${props.name}-async-typeAhead`;
  const { promiseInProgress } = usePromiseTracker({ area: trackerArea, delay: 0 });
  const [isNewSelectionApplied, setIsNewSelectionApplied] = useState<boolean>(false);
  const [selectedCountTag, setSelectedCountTag] = useState<string>(filterName);
  const [prevState, setPrevState] = useState<any>(selected);
  const [hoverFunnel, setHoverFunnel] = useState(false);
  const userPreferences = useSelector(selectUserPreference);
  const inboxFilters = userPreferences.inboxFilter?.value;
  const dispatch = useDispatch();
  const open = Boolean(anchorEl);
  const { classes } = useStyles({
    isSelected: isMultiSelect ? inboxFilters && inboxFilters.length > 0 : selected !== null,
    open,
    anchorRef,
    removeActionContainer,
  });

  const id = open ? `custom-type-ahead-${props.name}` : undefined;

  const fetchOptions = async request => {
    const response = await props.fetchOptions(request);
    setOptions(
      (response || []).map(opt => ({
        id: opt.value,
        name: opt.name,
        value: opt.value,
      })),
    );
  };

  const onApplyEvent = () => {
    setIsNewSelectionApplied(true);
    if (isArray(selected)) {
      const selectedValues = _.cloneDeep(selected).sort(function (a, b) {
        return a.value === b.value ? 0 : a.value > b.value ? 1 : -1;
      });
      props.apply(selectedValues);
    } else {
      props.apply(selected);
    }
    dispatch(notificationDataActions.setUnreadFilter(selected?.map(x => x.value)));
    dispatch(
      userDevicePreferenceActions.saveInboxFiltersAction({
        id: PreferenceCosmosKeys.inboxFilters,
        value: selected?.map(x => x.value),
      }),
    );
    closeActions();
  };

  const closeActions = () => {
    setAnchorEl(null);
    EnableAppScroll();
  };

  const cancelChanges = () => {
    props.onChange(prevState);
    closeActions();
  };

  const clearAll = () => props.onChange(isMultiSelect ? [] : null);

  const handleClose = () => {
    !isNewSelectionApplied && props.onChange(prevState);
    if (anchorEl) {
      anchorEl.focus();
    }
    closeActions();
  };

  const handleOpen = event => {
    if (!open) {
      trackPromise(fetchOptions(undefined), trackerArea);
      setAnchorEl(anchorRef ? anchorRef.current : event.currentTarget);
      setIsNewSelectionApplied(false);
      setPrevState(selected);

      DisableAppScroll();
    } else {
      if (props.hideApply) {
        onApplyEvent();
        return;
      }
      cancelChanges();
    }
  };

  const onClickAway = event => {
    if (props.hideApply) {
      onApplyEvent();
      return;
    }
    if (event.target.type !== 'submit' && event.target.id !== AVOID_CLICK_AWAY_LABEL) {
      !anchorRef && !isNewSelectionApplied && props.onChange(prevState);
      closeActions();
    }
  };

  const throttleService = useMemo(
    () => throttle(request => trackPromise(fetchOptions(request), trackerArea), 2000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const configureMultiSelectTag = () =>
    (options && options.length && selectAllValue && selected && selected.length === options.length - 1) ||
    (selected && selected.some(item => item.value === selectAllValue))
      ? `${t('search.filter.allTag')} ${filterName} (${options.length ? options.length - 1 : 0})`
      : selected && selected.length
      ? `(${selected.length}) ${filterName}`
      : filterName;

  useEffect(
    () => {
      if (
        isMultiSelect &&
        selectAllValue &&
        options &&
        selected?.length !== options.length &&
        selected?.some(item => item.value === selectAllValue)
      )
        props.onChange(_.cloneDeep(selected).splice(1, selected.length - 1));
      setSelectedCountTag(
        isMultiSelect ? configureMultiSelectTag() : selected ? `(1) ${filterName}` : filterName + ' '.repeat(6),
      );
      return EnableAppScroll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selected), JSON.stringify(options)],
  );

  useEffect(() => {
    if ((providedOptions || []).length > 0 && !props.fetchOptions) {
      setOptions(providedOptions);
    }
  }, [props.fetchOptions, providedOptions]);

  return (
    <Box>
      <div className={classes.root}>
        <Button
          onMouseEnter={() => setHoverFunnel(true)}
          onMouseLeave={() => setHoverFunnel(false)}
          disableRipple
          variant="outlined"
          aria-describedby={id}
          disabled={!!isDisabled}
          classes={{ root: inboxFilters?.length === 0 ? classes.filterIcon : classes.filterApplied }}
          onClick={handleOpen}
          id={props.anchorAccesorid}
        >
          <FilterAltOutlinedIcon />
        </Button>
      </div>
      <Popper
        className={customClass ? `${customClass}` : `${classes.popper}`}
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={onClickAway}>
            <Fade {...TransitionProps} in={open} timeout={300}>
              <Paper aria-label={id} classes={{ root: classes.paperDropdown }}>
                <Grid
                  item
                  container
                  justifyContent={isMultiSelect ? 'space-between' : 'flex-end'}
                  alignItems="center"
                  classes={{
                    root: searchBoxHiddenWithSingleOption
                      ? classes.headerContainerNoSearchBoxSingleOption
                      : classes.headerContainer,
                  }}
                >
                  {isMultiSelect ? (
                    <div className={classes.countTag}>{`${
                      selectAllValue &&
                      options &&
                      selected &&
                      selected.length &&
                      options?.length &&
                      selected.length === options?.length
                        ? options?.length - 1
                        : selected?.length || 0
                    } ${t('search.filter.Selected')}`}</div>
                  ) : null}
                  {!props.hideClearAll ? (
                    <Button
                      className="clear-all-button"
                      aria-label="clear-all-button"
                      variant="text"
                      color="primary"
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      onClick={() => clearAll()}
                      disabled={!isMultiSelect}
                      classes={{ text: ` ${classes.button} ${classes.clearAllButton} ` }}
                    >
                      {t('search.filter.clearAll')}
                    </Button>
                  ) : null}
                </Grid>
                <CustomTypeAhead
                  enableCustomScroll
                  removeCloseIcon
                  variant="outlined"
                  size="small"
                  handleClose={handleClose}
                  options={options}
                  isLoading={promiseInProgress}
                  onInputChange={throttleService}
                  hiddenInput={hiddenInput}
                  getOptionDisabled={getOptionDisabled}
                  unreadInboxCheck={true}
                  customClasses={{ option: classes.listItem }}
                  {...props}
                />
                {!props.hideApply ? (
                  <div className={classes.actionContainer}>
                    <Grid item container justifyContent="flex-end" alignItems="center">
                      <Button
                        className="clear-all-button"
                        aria-label="cancel-button"
                        variant="text"
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        onClick={() => cancelChanges()}
                        classes={{ text: `${classes.button} ${classes.actionButton} ${classes.inActive}` }}
                      >
                        {t('search.filter.cancel')}
                      </Button>
                      <Button
                        className="clear-all-button"
                        aria-label="apply-button"
                        variant="text"
                        color='primary'
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        onClick={() => onApplyEvent()}
                        classes={{ text: `${classes.button} ${classes.actionButton}` }}
                        id={props.hiddenApplyBtn}
                      >
                        {t('search.filter.apply')}
                      </Button>
                    </Grid>
                  </div>
                ) : null}
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
};
